import { Close, TaskAlt } from '@mui/icons-material';
import { Autocomplete, Chip, IconButton, Menu, MenuItem } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { Control, Controller } from 'react-hook-form';

import { Input } from '@/components/Input';
import { InputWrapper } from '@/components/InputWrapper/InputWrapper';
import { useTaskStore } from '@/features/Task';
import { getTrackerUrl } from '@/utils/getTrackerUrl';

import { FormFields, FormValues } from '../../types';

import s from './TaskSelector.module.scss';

const FAVORITES_VISIBLE_COUNT = 4;

type TaskSelectorProps = {
  control: Control<FormValues>;
  watchedTaskLabel: string;
  isTaskValid: boolean;
  errors: any;
  suggestions: any[];
  setValue: (name: any, value: any) => void;
  taskKey: string;
  isEdit?: boolean;
  isCopy?: boolean;
  isTaskSelected?: boolean;
};

export const TaskSelector: React.FC<TaskSelectorProps> = observer(
  ({
    control,
    watchedTaskLabel,
    isTaskValid,
    errors,
    suggestions,
    setValue,
    taskKey,
    isEdit,
    isCopy,
    isTaskSelected,
  }) => {
    const [isCleared, setIsCleared] = useState(false);
    const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
    const { favoriteTasks } = useTaskStore();

    const handleSelectFavorite = (task: { key: string; summary: string }) => {
      setValue(FormFields.taskLabel, `${task.key} — ${task.summary}`);
      setValue(FormFields.taskKey, task.key);
    };

    const visibleFavorites = favoriteTasks.slice(0, FAVORITES_VISIBLE_COUNT);
    const hiddenFavorites = favoriteTasks.slice(FAVORITES_VISIBLE_COUNT);

    const handleSelectHiddenFavorite = (task: { key: string; summary: string }) => {
      handleSelectFavorite(task);
      setMoreAnchor(null);
    };

    const showAutocomplete = !isEdit && !isCopy && (!isTaskSelected || isCleared);

    if (showAutocomplete) {
      return (
        <InputWrapper label="Задача">
          <Controller
            control={control}
            name={FormFields.taskLabel}
            render={({ field }) => (
              <Autocomplete
                freeSolo
                options={suggestions}
                filterOptions={(options) => options}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : `${option.key} — ${option.summary}`
                }
                inputValue={field.value}
                onInputChange={(_, newInputValue, reason) => {
                  const shouldClear = reason === 'clear' || reason === 'reset';
                  field.onChange(shouldClear ? '' : newInputValue);
                  setValue(FormFields.taskKey, '');
                }}
                onChange={(_, value) => {
                  if (!value || typeof value === 'string') {
                    field.onChange(value ?? '');
                    setValue(FormFields.taskKey, '');

                    return;
                  }

                  field.onChange(`${value.key} — ${value.summary}`);
                  setValue(FormFields.taskKey, value.key);
                }}
                renderInput={(params) => (
                  <Input
                    {...params}
                    autoFocus
                    placeholder="Выберите задачу"
                    error={!!errors.taskLabel || (!isTaskValid && !!watchedTaskLabel.trim())}
                    helperText={
                      errors.taskLabel?.message ||
                      (!isTaskValid && !!watchedTaskLabel.trim() ? 'Выберите задачу' : '')
                    }
                  />
                )}
              />
            )}
            rules={{ required: 'Введите задачу' }}
          />
          {favoriteTasks.length > 0 && (
            <div className={s.favorites}>
              <span className={s.favorites__label}>Избранные:</span>
              {visibleFavorites.map((task) => (
                <Chip
                  key={task.id}
                  size="small"
                  label={task.key}
                  title={task.summary}
                  onClick={() => handleSelectFavorite(task)}
                />
              ))}
              {hiddenFavorites.length > 0 && (
                <>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`+${hiddenFavorites.length}`}
                    onClick={(e) => setMoreAnchor(e.currentTarget)}
                  />
                  <Menu
                    anchorEl={moreAnchor}
                    open={!!moreAnchor}
                    onClose={() => setMoreAnchor(null)}
                    slotProps={{ paper: { sx: { maxWidth: 'min(360px, calc(100vw - 32px))' } } }}
                  >
                    {hiddenFavorites.map((task) => (
                      <MenuItem key={task.id} onClick={() => handleSelectHiddenFavorite(task)}>
                        <span className={s.favorites__option}>
                          {task.key} — {task.summary}
                        </span>
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </div>
          )}
        </InputWrapper>
      );
    }

    const handleClear = () => {
      setValue(FormFields.taskKey, '');
      setValue(FormFields.taskLabel, '');
      setIsCleared(true);
    };

    return (
      <div className={s.display_default}>
        <div className={s.display_default__header}>
          <div className={s.display_default__center}>
            <TaskAlt fontSize="small" style={{ color: 'var(--dark-secondary-color)' }} />
            <span className={s.display_default__label}>Задача:</span>
          </div>
          {!isEdit && (
            <IconButton size="small" onClick={handleClear} title="Сменить задачу">
              <Close fontSize="small" />
            </IconButton>
          )}
        </div>
        <span className={s.display_default__value}>
          <a href={getTrackerUrl(taskKey)} target="_blank" rel="noopener noreferrer">
            {watchedTaskLabel}
          </a>
        </span>
      </div>
    );
  },
);
